import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import * as Handlebars from 'handlebars/dist/cjs/handlebars';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { Widget, Dashboard, TimeSeriesDetailRequest } from 'src/api/models';
import { WidgetTextTemplate } from 'src/api/models/widget-text-template';
import { IWidgetEditor, WidgetType, TimeSeriesDetail } from "src/app/models/dashboard";
import { DataService } from 'src/api/services/data.service';
import { WidgetTextTemplateService } from 'src/api/services/widget-text-template.service';
import { ToastrService } from 'ngx-toastr';
import { ApplicationConfigService } from 'src/service/application-config.service';

declare const monaco: any;


@Component({
  selector: 'app-free-text-widget-editor',
  templateUrl: './free-text-widget-editor.component.html',
  styleUrls: ['./free-text-widget-editor.component.css']
})
export class FreeTextWidgetEditorComponent implements OnInit, OnDestroy, IWidgetEditor {
  @ViewChild('editorTabs', { static: false }) editorTabs: TabsetComponent;

  onHtmlEditorInit(editor: any): void {
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
      () => this.formatHtml()
    );
  }

  public widgetType: WidgetType;
  public widget: Widget;
  public dashboard: Dashboard;
  public siteUri: string;
  public w: any;
  public active: TimeSeriesDetail;
  public example: string = "<div class=\"card\">\n  <div class=\"card-body\">\n      <div class=\"h3\">{{data.db_decoded.deviceType}}</div>\n        <ul>\n{{#each data.calculations}}\n         <li>{{this.Duration}}</li>\n{{/each}}\n      </ul>\n      <div>Wind Speed:{{data.db_decoded.windSpeed}}</div>\n      <div>Meta Data:{{metaData.siteType}}</div>\n      <div>Latitude:{{latitude}}</div>\n      <div>Longitude:{{longitude}}</div>\n  </div>\n  <div class=\"card-arrow\">\n      <div class=\"card-arrow-top-left\"></div>\n      <div class=\"card-arrow-top-right\"></div>\n      <div class=\"card-arrow-bottom-left\"></div>\n      <div class=\"card-arrow-bottom-right\"></div>\n  </div>\n</div>";

  public previewHtml: string = '';
  public previewData: any = null;
  public isLoadingPreview: boolean = false;
  public templatesLoading: boolean = false;
  public templateTypeFilter: number = 0;
  public selectedTemplate: WidgetTextTemplate | null = null;
  public templates: WidgetTextTemplate[] = [];
  public previewImageUrls = new Map<number, SafeUrl>();
  private previewObjectUrls = new Map<number, string>();

  get filteredTemplates(): WidgetTextTemplate[] {
    return this.templates.filter(t => t.type === this.templateTypeFilter);
  }

  /*
  OLD_HARDCODED_TEMPLATES_START
    {
      name: 'Simple Value',
      description: 'A centered card displaying a single value with label and unit.',
  OLD_HARDCODED_TEMPLATES_END
  */

  constructor(
    private dataService: DataService,
    private toastr: ToastrService,
    private app: ApplicationConfigService,
    private templateService: WidgetTextTemplateService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.w = <any>this.widget;
    if (this.w.timeSeriesList == null) {
      this.w.timeSeriesList = [{transforms: []}];
    }
    this.loadTemplates();
  }

  private loadTemplates(): void {
    this.templatesLoading = true;
    this.templateService.WidgetTextTemplateGetAll({ isActive: true }).subscribe({
      next: (templates) => {
        this.templates = templates;
        this.templatesLoading = false;
        templates.filter(t => t.hasPreviewImage).forEach(t => {
          this.templateService.WidgetTextTemplateGetPreviewImage(t.id).subscribe({
            next: (blob) => {
              const objectUrl = URL.createObjectURL(blob);
              this.previewObjectUrls.set(t.id, objectUrl);
              this.previewImageUrls.set(t.id, this.sanitizer.bypassSecurityTrustUrl(objectUrl));
            }
          });
        });
      },
      error: () => {
        this.toastr.error('Failed to load widget templates.');
        this.templatesLoading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.previewObjectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  public refreshPreview(): void {
    try {
      const template = Handlebars.compile(this.w.html || '');
      this.previewHtml = template(this.previewData);
    } catch (e: any) {
      this.previewHtml = `<div class="alert alert-danger"><strong>Template error:</strong> ${e.message}</div>`;
    }
  }

  public loadPreviewData(): void {
    const ts = this.w.timeSeriesList[0];
    if (!ts?.uri) {
      this.toastr.warning('Please configure a time series URI first.');
      return;
    }

    this.isLoadingPreview = true;

    const request: TimeSeriesDetailRequest = {
      index: 0,
      forceEmbed: false,
      period: this.w.period || null,
      timeSeries: {
        single: {
          variable: {
            uri: ts.uri,
            tagMatches: ts.tagMatches || null,
            transforms: ts.transforms?.length > 0 ? ts.transforms : null
          }
        },
        virtualTimeSeriesId: ts.virtualTimeSeriesId || null
      },
      siteUri: ts.siteUri || this.siteUri,
      siteType: this.dashboard.siteType || '',
      type: ts.sourceType || 'TimeSeries',
      timeZoneOffset: this.app.getTimeZoneOffset(this.dashboard.timeZone),
      publishedIdentifier: this.dashboard.publishedIdentifier || null
    };

    this.dataService.DataDashboardGets({
      customerId: this.dashboard.customerId,
      dashboardId: this.dashboard.dashboardId,
      requests: [request]
    }).subscribe({
      next: (results) => {
        this.isLoadingPreview = false;
        if (results?.length > 0 && results[0].results?.length > 0) {
          const series = results[0].results[0];
          const last: any = series.values?.length > 0 ? series.values[series.values.length - 1] : null;
          let data: any = last?.value;
          if (data && typeof data === 'string' && data.startsWith('{')) {
            try { data = JSON.parse(data); } catch { }
          }
          this.previewData = {
            series,
            data,
            latitude: series.latitude,
            longitude: series.longitude,
            metaData: series.metaData
          };
        } else {
          this.toastr.info('No data returned for the configured time series.');
        }
        this.refreshPreview();
      },
      error: () => {
        this.isLoadingPreview = false;
        this.toastr.error('Failed to load preview data.');
      }
    });
  }

  public formatHtml(): void {
    if (!this.w?.html) return;
    this.w.html = this.beautifyHtml(this.w.html);
  }

  private beautifyHtml(html: string): string {
    const INDENT = '  ';
    const voidTags = new Set([
      'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
      'link', 'meta', 'param', 'source', 'track', 'wbr'
    ]);

    // Tokenize: Handlebars expressions first, then HTML tags, then text.
    // This order ensures '>' inside {{#if x > 0}} isn't mistaken for a tag boundary.
    const tokenRegex = /(\{\{[\s\S]*?\}\})|(<!--[\s\S]*?-->)|(<[^>]*>)|([^<{]+|\{(?!\{))/g;

    interface Token { kind: 'open' | 'close' | 'void' | 'text' | 'hbs-open' | 'hbs-close' | 'hbs'; raw: string; }
    const tokens: Token[] = [];
    let m: RegExpExecArray | null;

    while ((m = tokenRegex.exec(html)) !== null) {
      const raw = m[0];
      if (m[1]) {
        // Handlebars expression
        if (/^\{\{#/.test(raw))       tokens.push({ kind: 'hbs-open',  raw });
        else if (/^\{\{\//.test(raw)) tokens.push({ kind: 'hbs-close', raw });
        else                           tokens.push({ kind: 'hbs',        raw });
      } else if (m[2]) {
        // HTML comment — keep as-is
        tokens.push({ kind: 'text', raw: raw.trim() });
      } else if (m[3]) {
        // HTML tag
        const tagName = (raw.match(/^<\/?([a-zA-Z][a-zA-Z0-9:]*)/) ?? [])[1]?.toLowerCase() ?? '';
        if (raw.startsWith('</'))                          tokens.push({ kind: 'close', raw });
        else if (raw.endsWith('/>') || voidTags.has(tagName)) tokens.push({ kind: 'void',  raw });
        else                                               tokens.push({ kind: 'open',  raw });
      } else if (m[4]) {
        // Plain text — skip whitespace-only chunks
        const trimmed = raw.trim();
        if (trimmed) tokens.push({ kind: 'text', raw: trimmed });
      }
    }

    // Render with indentation
    let depth = 0;
    const lines: string[] = [];

    for (const tok of tokens) {
      switch (tok.kind) {
        case 'close':
        case 'hbs-close':
          depth = Math.max(0, depth - 1);
          lines.push(INDENT.repeat(depth) + tok.raw);
          break;
        case 'open':
        case 'hbs-open':
          lines.push(INDENT.repeat(depth) + tok.raw);
          depth++;
          break;
        default:
          lines.push(INDENT.repeat(depth) + tok.raw);
      }
    }

    return lines.join('\n');
  }

  public getPreviewImageUrl(id: number): SafeUrl | null {
    return this.previewImageUrls.get(id) ?? null;
  }

  public viewTemplate(template: WidgetTextTemplate): void {
    this.selectedTemplate = template;
  }

  public closeTemplateDetail(): void {
    this.selectedTemplate = null;
  }

  public applyTemplate(template: WidgetTextTemplate): void {
    if (this.w.html && !confirm(`Replace the current HTML with the "${template.name}" template?`)) {
      return;
    }
    this.w.html = template.html;
    this.selectedTemplate = null;
    if (this.editorTabs?.tabs?.length > 0) {
      this.editorTabs.tabs[0].active = true;
    }
  }

  public selectedPrimary(value: any): void {
    this.w.timeSeries.uri = value;
  }
}
